import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CHUGUMI_RESULTS, calculateChugumiResult } from '../../data/chugumiTraits';
import { getResultDetail, saveChugumiResult } from '../../client';
import { useContent } from '../../content';
import VibeMap from '../../components/VibeMap';
import { useTranslation } from 'react-i18next';
import { localize } from '../../i18n/locale';
import { trackEvent, trackEventOnce } from '../../utils/analytics';
import { useReportAnalytics } from '../../hooks/useReportAnalytics';

const Result = () => {
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const language = i18n.resolvedLanguage || i18n.language;
    const tr = (ko, en) => localize(language, { ko, en });
    const { id } = useParams();
    const { getC, loaded: contentLoaded } = useContent(['chugumi']);
    const [result, setResult] = useState(null);
    const [scores, setScores] = useState({ vibe: 0, identity: 0 });
    const [resultKey, setResultKey] = useState(null);
    const reportRootRef = useRef(null);
    const resultViewTrackedRef = useRef('');

    useReportAnalytics({
        rootRef: reportRootRef,
        analysisType: 'chugumi',
        resultKey: String(id || resultKey || 'chugumi-result'),
        enabled: Boolean(result && resultKey && contentLoaded),
    });

    useEffect(() => {
        if (!result || !resultKey || !contentLoaded) return;
        const analyticsKey = String(id || resultKey);
        if (resultViewTrackedRef.current === analyticsKey) return;
        resultViewTrackedRef.current = analyticsKey;
        trackEventOnce('result_viewed', `chugumi:${analyticsKey}`, {
            analysis_type: 'chugumi',
            result_type: resultKey,
            is_paid_result: false,
            entry_source: id ? 'saved_result' : 'new_analysis',
        });
    }, [contentLoaded, id, result, resultKey]);

    const asString = (value, fallback = '') => (typeof value === 'string' && value.trim() ? value : fallback);
    const asArray = (value, fallback = []) => (Array.isArray(value) ? value : fallback);

    useEffect(() => {
        if (id) {
            const loadHistory = async () => {
                const data = await getResultDetail(id);
                if (data && data.payload) {
                    const topTag = data.payload.resultKey || data.result_label || data.payload.topTag;
                    setResult(CHUGUMI_RESULTS[topTag] || Object.values(CHUGUMI_RESULTS)[0]);
                    setResultKey(topTag);
                    const scoreData = data.payload.scores || data.summary?.scores || {};
                    setScores({
                        vibe: scoreData.vibe || data.payload.vibeScore || 0,
                        identity: scoreData.identity || data.payload.identityScore || 0
                    });
                } else {
                    navigate('/mypage');
                }
            };
            loadHistory();
            return;
        }

        const savedAnswers = localStorage.getItem('chugumiTestAnswers');

        if (!savedAnswers) {
            navigate('/chugumi-test');
            return;
        }

        const answers = JSON.parse(savedAnswers);

        // 새로운 5단계 척도 계산 로직 적용
        const calculation = calculateChugumiResult(answers);
        const { resultKey: calcResultKey, vibeScore, identityScore, result: finalResult } = calculation;

        setResult(finalResult);
        setResultKey(calcResultKey);
        setScores({ vibe: vibeScore, identity: identityScore });

        // Save to backend
        const saveResult = async () => {
            const payload = {
                result_key: calcResultKey,
                resultKey: calcResultKey, // 호환성을 위해 추가
                scores: {
                    vibe: vibeScore,
                    identity: identityScore,
                    max: 40,
                    axes: [
                        { key: 'vibe', label: 'Vibe', score: vibeScore },
                        { key: 'identity', label: 'Identity', score: identityScore }
                    ]
                },
                raw: {
                    vibeScore,
                    identityScore,
                    answers,
                    resultKey: calcResultKey, // 히스토리 로딩을 위해 추가
                    scores: { vibe: vibeScore, identity: identityScore }
                }
            };

            console.log("Saving Chugumi result...", payload);
            await saveChugumiResult(payload);
            console.log("Chugumi result saved.");
            // 다음 테스트를 위해 답변 초기화
            localStorage.removeItem('chugumiTestAnswers');
        };
        saveResult();

    }, [navigate, id]);

    if (!result || !contentLoaded) return null;

    // Get content from content layer with fallback to hardcoded data
    const title = contentLoaded && resultKey
        ? asString(getC(`chugumi.results.${resultKey}.title`), result.title)
        : result.title;
    const summary = contentLoaded && resultKey
        ? asString(getC(`chugumi.results.${resultKey}.summary`), result.summary)
        : result.summary;
    const character = contentLoaded && resultKey
        ? asString(getC(`chugumi.results.${resultKey}.character`), result.character)
        : result.character;
    const style = contentLoaded && resultKey
        ? asString(getC(`chugumi.results.${resultKey}.style`), result.style)
        : result.style;
    const hashtags = contentLoaded && resultKey
        ? asArray(getC(`chugumi.results.${resultKey}.hashtags`), result.hashtags || [])
        : (result.hashtags || []);

    const vibePercent = ((scores.vibe - 8) / 32) * 100;
    const identityPercent = ((scores.identity - 8) / 32) * 100;

    const sebCode = (scores.vibe > 24 ? 'H' : 'L') + (scores.identity > 24 ? 'H' : 'L') + (scores.vibe + scores.identity > 48 ? 'H' : 'L');
    const syncRate = Math.floor((scores.vibe + scores.identity) / 80 * 15 + 75); // 75~90%

    // Convert scores to -50~50 range for VibeMap
    const targetCoords = {
        x: (scores.vibe - 24) * (50 / 16),
        y: (scores.identity - 24) * (50 / 16)
    };

    return (
        <div className="result-container">
            <div ref={reportRootRef} className="result-card">
                <div className="badge">{tr('내면 성향 분석 (SEB) 코드', 'Inner Preference Code')}: {sebCode}</div>
                <h1 style={{ fontSize: '2rem', marginBottom: '1rem', lineHeight: '1.4' }}>
                    {tr('당신은 내면적으로', 'Your inner preference is')}<br /> <strong style={{ color: '#60a5fa' }}>[{title}]</strong>형 리더입니다
                </h1>

                <div className="image-container">
                    <div className="image-box">
                        <img
                            src={result.image}
                            alt={title}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'https://via.placeholder.com/300x300?text=Result+Image';
                                e.target.parentElement.style.backgroundColor = '#334155';
                            }}
                        />
                    </div>
                </div>

                <div className="hashtags">
                    {hashtags.map((tag, i) => (
                        <span key={i} className="tag">{tag}</span>
                    ))}
                </div>

                <div data-analytics-section="summary" className="section" style={{ border: '1px solid rgba(96, 165, 250, 0.3)', background: 'linear-gradient(135deg, rgba(30,41,59,0.5), rgba(15,23,42,0.8))' }}>
                    <h3 style={{ color: '#60a5fa', marginBottom: '1rem' }}>{tr('🧭 Vibe Coordinate (나의 취향 좌표)', '🧭 Vibe Coordinate (My Preference)')}</h3>
                    <p style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>{tr('얼굴 골격, 분위기, 취향을 수치화한 2D 맵 상에서 당신이 선호하고 지향하는 목표점(Target)입니다.', 'Your target point on a 2D map of facial structure, mood, and aesthetic preference.')}</p>
                    <div style={{ transform: 'scale(0.8)', transformOrigin: 'top center', marginBottom: '-50px' }}>
                        <VibeMap
                            physical={{ x: targetCoords.x, y: targetCoords.y }}
                            target={targetCoords}
                            matchType="Target"
                        />
                    </div>
                </div>

                <div className="section">
                    <h3>{tr('💡 성향 요약', '💡 Preference Summary')}</h3>
                    <p>{summary}</p>
                </div>

                <div className="section">
                    <h3>{tr('👀 캐릭터 특성', '👀 Character Traits')}</h3>
                    <p>{character}</p>
                </div>

                <div data-analytics-section="recommendation" className="section highlight">
                    <h3>{tr('✂️ 추천 스타일', '✂️ Recommended Style')}</h3>
                    <p>{style}</p>
                </div>

                <div className="bridge-section" style={{ marginTop: '3rem', padding: '1.5rem', borderRadius: '16px', background: 'rgba(236, 72, 153, 0.05)', border: '1px solid rgba(236, 72, 153, 0.2)' }}>
                    <div style={{ color: '#f472b6', fontWeight: 'bold', fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                        {tr(`현재 당신의 얼굴 골격(Base)은 이 스타일과 약 ${syncRate}%의 잠재적 싱크로율을 보입니다.`, `Your facial structure shows an estimated ${syncRate}% potential match with this style.`)}
                    </div>
                    <p style={{ color: '#cbd5e1', fontSize: '0.9rem', marginBottom: '1.5rem' }}>{tr('나의 실제 얼굴 데이터와 결합하여 내게 딱 맞는 최적의 스타일을 찾아보세요.', 'Combine this result with your face data to find a style tailored to you.')}</p>

                    <button
                        onClick={() => {
                            trackEvent('result_action_clicked', {
                                analysis_type: 'chugumi',
                                action_name: 'view_detail',
                                source_section: 'recommendation',
                            });
                            navigate('/info');
                        }}
                        style={{
                            background: 'linear-gradient(90deg, #f43f5e, #8b5cf6)',
                            color: 'white',
                            border: 'none',
                            padding: '1.2rem 2rem',
                            borderRadius: '100px',
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            boxShadow: '0 10px 20px rgba(244, 63, 94, 0.3)',
                            width: '100%',
                            transition: 'all 0.3s ease'
                        }}
                    >
                        {tr('이 스타일을 내 얼굴에 이식할 수 있을까? 👉', 'Can this style work with my face? 👉')}
                    </button>
                </div>

                <div className="action-buttons">
                    <button className="restart-btn" onClick={() => {
                        trackEvent('result_action_clicked', {
                            analysis_type: 'chugumi',
                            action_name: 'retry',
                            source_section: 'result_footer',
                        });
                        localStorage.removeItem('chugumiTestAnswers');
                        navigate('/chugumi-test');
                    }}>
                        {tr('성향 테스트 다시하기', 'Retake Preference Test')}
                    </button>
                    <button className="home-btn" onClick={() => navigate('/')}>
                        {tr('홈으로 가기', 'Go Home')}
                    </button>
                </div>
            </div>

            <style jsx>{`
                .result-container {
                    min-height: 100vh;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 2rem 1rem;
                    background: linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%);
                    color: white;
                }

                .result-card {
                    background: rgba(255, 255, 255, 0.05);
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 24px;
                    padding: 3rem 2rem;
                    width: 100%;
                    max-width: 600px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.3);
                    text-align: center;
                }

                .badge {
                    display: inline-block;
                    background: rgba(59, 130, 246, 0.2);
                    color: #60a5fa;
                    padding: 0.5rem 1rem;
                    border-radius: 100px;
                    font-size: 0.9rem;
                    font-weight: 600;
                    margin-bottom: 1rem;
                }

                h1 {
                    font-size: 2.5rem;
                    font-weight: 800;
                    margin-bottom: 2rem;
                    background: linear-gradient(to right, #fff, #94a3b8);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }

                .image-container {
                    margin-bottom: 2rem;
                    display: flex;
                    justify-content: center;
                }

                .image-box {
                    width: 250px;
                    height: 250px;
                    border-radius: 20px;
                    overflow: hidden;
                    box-shadow: 0 10px 20px rgba(0,0,0,0.2);
                    background: #1e293b;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .image-box img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }

                .hashtags {
                    display: flex;
                    flex-wrap: wrap;
                    justify-content: center;
                    gap: 0.5rem;
                    margin-bottom: 2.5rem;
                }

                .tag {
                    background: rgba(255,255,255,0.1);
                    padding: 0.4rem 0.8rem;
                    border-radius: 8px;
                    font-size: 0.9rem;
                    color: #e2e8f0;
                }

                .section {
                    text-align: left;
                    margin-bottom: 2rem;
                    padding: 1.5rem;
                    background: rgba(255,255,255,0.03);
                    border-radius: 16px;
                }

                .section.highlight {
                    background: rgba(59, 130, 246, 0.1);
                    border: 1px solid rgba(59, 130, 246, 0.2);
                }

                .section h3 {
                    font-size: 1.1rem;
                    color: #fff;
                    margin-bottom: 0.5rem;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .section p {
                    color: #cbd5e1;
                    line-height: 1.6;
                    margin: 0;
                }

                .action-buttons {
                    display: flex;
                    gap: 1rem;
                    margin-top: 2rem;
                }

                .face-btn, .restart-btn, .home-btn {
                    flex: 1;
                    padding: 1rem;
                    border-radius: 12px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .face-btn {
                    background: linear-gradient(135deg, #3b82f6, #8b5cf6);
                    color: white;
                    border: none;
                    box-shadow: 0 4px 15px rgba(59, 130, 246, 0.4);
                }

                .face-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.6);
                }

                .restart-btn {
                    background: white;
                    color: #0f172a;
                    border: none;
                }

                .home-btn {
                    background: transparent;
                    color: white;
                    border: 1px solid rgba(255,255,255,0.2);
                }

                .restart-btn:hover {
                    background: #f1f5f9;
                }

                .home-btn:hover {
                    background: rgba(255,255,255,0.05);
                }

                @media (max-width: 640px) {
                    .action-buttons {
                        flex-direction: column;
                    }
                }
            `}</style>
        </div>
    );
};

export default Result;
